<!--
	Invitation à recevoir nos emails, proposée UNE fois, après que
	l'utilisateur a vu ce que l'outil produit.

	Pourquoi pas à l'inscription : à ce moment-là il ne connaît pas encore le
	produit, et le taux plafonne (7 % en août 2026). Ici la valeur est déjà
	perçue — d'où le seuil d'au moins une génération.

	Deux finalités, chacune sa case, jamais pré-cochée (RGPD : consentement
	distinct par finalité). On ne propose que ce qui n'est pas encore consenti,
	et « Non merci » ne retire rien : ne pas consentir n'est pas se désabonner.

	Monté une seule fois dans le layout racine : aucun générateur n'a à s'en
	occuper. L'attente démarre quand le compte DEVIENT éligible (première
	génération terminée, ou chargement pour un revenant) — pas au montage du
	layout. L'ancienne version armait un timer unique 12 s après le premier
	chargement : pour un nouvel inscrit, à cet instant-là il était encore sur
	la landing derrière la bannière cookies, et la session était perdue. Seuls
	17 % des comptes à session unique la voyaient (mesure du 8 sept. 2026).

	Le marqueur `consent_prompt_shown_at` est posé après 10 s d'affichage
	effectif, ou à la réponse, oui comme non : c'est la demande unique du
	compte. Un affichage avorté (départ avant) ne la consomme pas ; relancer
	quelqu'un qui a refusé, c'est exactement ce qui fait cocher « spam ».

	C'est l'élément le moins prioritaire de l'écran : il ne doit JAMAIS disputer
	sa place à autre chose. D'où trois précautions, chacune contre une collision
	constatée :
	  - coin bas GAUCHE. Le coin bas droit appartient aux toasts (z-100), et le
	    toast « Séquence générée » sort exactement au moment où l'invitation
	    voudrait apparaître — il recouvrait le bouton d'acceptation.
	  - délai avant affichage, le temps que le toast s'efface et que l'écran
	    redevienne calme.
	  - on ne s'affiche pas par-dessus la bannière cookies (bas de page, pleine
	    largeur) ni par-dessus une modale plein écran (CrossSellPrompt sur
	    /journal et /app/sequences, réglages, etc.). Tant qu'un de ces calques
	    occupe l'écran, on revérifie toutes les 5 s sans rien afficher.
	Et seulement sur les surfaces applicatives : sur mobile le bas de l'écran
	appartient à la barre de navigation du cahier journal (z-30) et à la barre
	CTA de la landing (z-50) — d'où le décalage vertical et le filtrage des
	routes. Demander une inscription newsletter sur la page d'accueil marketing
	n'aurait de toute façon aucun sens.

	Événements funnel : `consent_prompt_blocked` (une fois par session, avec le
	calque en cause) et `consent_prompt_shown` — pour distinguer « pas
	éligible », « éligible mais garde-fou » et « affiché ».
-->
<script lang="ts">
	import { untrack } from 'svelte';
	import { page } from '$app/stores';
	import { authState, refreshSubscriptionData } from '$lib/stores/auth.svelte';
	import { updateConsent, markConsentPromptSeen, trackFunnelEvent } from '$lib/api';
	import { totalGenerations } from '$lib/quotas';
	import { X } from 'lucide-svelte';

	const { user, subscription } = authState;

	/** Laisse au toast de fin de génération le temps de s'effacer. */
	const DELAI_MS = 12_000;
	/** Écran occupé (cookies, modale) : on revérifie, sans rien afficher. */
	const RELANCE_MS = 5_000;
	/** Au-delà, l'invitation a été vue : c'était la demande unique du compte. */
	const VUE_APRES_MS = 10_000;

	let dismissed = $state(false);
	let saving = $state(false);
	let accepted = $state(false);
	let ecranCalme = $state(false);
	/** Marqueur posé pendant cette session : le store peut être en retard. */
	let marqueurPose = $state(false);
	let nouveautes = $state(false);
	let offres = $state(false);

	/** Surfaces où l'utilisateur travaille — pas les pages marketing. */
	const surfaceApp = $derived(
		['/app', '/journal', '/generateur-bulletin'].some(
			(p) => $page.url.pathname === p || $page.url.pathname.startsWith(p + '/')
		)
	);

	/** Finalités qu'il reste à proposer — jamais celles déjà consenties. */
	const finalites = $derived({
		nouveautes: !!$subscription && !$subscription.product_updates_consent,
		offres: !!$subscription && !$subscription.marketing_consent
	});

	const eligible = $derived(
		surfaceApp &&
			!dismissed &&
			!!$user &&
			!!$subscription &&
			(accepted ||
				((finalites.nouveautes || finalites.offres) &&
					(!$subscription.consent_prompt_shown_at || marqueurPose) &&
					totalGenerations($subscription) >= 1))
	);

	const visible = $derived(eligible && ecranCalme);

	function calqueOccupant(): string | null {
		// Bannière cookies encore à l'écran : elle occupe tout le bas.
		if (!localStorage.getItem('cookie-consent')) return 'cookie_banner';
		// Modale plein écran ouverte : on ne s'invite pas derrière son voile.
		if (document.querySelector('.fixed.inset-0')) return 'overlay';
		return null;
	}

	let blocageSignale = false;

	$effect(() => {
		if (!eligible || ecranCalme) return;
		let timer = setTimeout(function verifier() {
			const raison = calqueOccupant();
			if (!raison) {
				ecranCalme = true;
				return;
			}
			if (!blocageSignale) {
				blocageSignale = true;
				trackFunnelEvent('consent_prompt_blocked', { reason: raison });
			}
			timer = setTimeout(verifier, RELANCE_MS);
		}, DELAI_MS);
		return () => clearTimeout(timer);
	});

	let affichageSignale = false;

	$effect(() => {
		if (!visible || accepted || marqueurPose) return;
		if (!affichageSignale) {
			affichageSignale = true;
			const f = untrack(() => finalites);
			trackFunnelEvent('consent_prompt_shown', { nouveautes: f.nouveautes, offres: f.offres });
		}
		const timer = setTimeout(() => {
			marqueurPose = true;
			markConsentPromptSeen().catch(() => {});
		}, VUE_APRES_MS);
		return () => clearTimeout(timer);
	});

	async function respond(optIn: boolean) {
		if (saving) return;
		saving = true;
		try {
			if (optIn) {
				const state = await updateConsent(
					{
						...(nouveautes ? { product_updates_consent: true } : {}),
						...(offres ? { marketing_consent: true } : {})
					},
					'prompt'
				);
				// Le backend peut refuser (désabonnement antérieur depuis un email) :
				// on ne remercie que si au moins un abonnement est réellement pris.
				accepted =
					(nouveautes && state.product_updates_consent) || (offres && state.marketing_consent);
			}
			await markConsentPromptSeen();
			marqueurPose = true;
			await refreshSubscriptionData();
		} catch {
			// Échec réseau : on referme sans rien promettre. Le marqueur n'étant
			// pas posé, l'invitation pourra revenir à la prochaine session.
		} finally {
			saving = false;
			if (!accepted) dismissed = true;
			else setTimeout(() => (dismissed = true), 2600);
		}
	}
</script>

{#if visible}
	<div
		class="fixed bottom-20 left-4 right-4 z-40 rounded-sm border border-mist bg-paper p-5 shadow-overlay sm:bottom-4 sm:right-auto sm:max-w-sm"
		role="dialog"
		aria-label="Recevoir nos emails"
	>
		{#if accepted}
			<p class="text-sm font-medium text-ink">C'est noté, merci.</p>
			<p class="mt-1 text-sm leading-relaxed text-ash">
				Vous pouvez changer d'avis à tout moment depuis Mon espace.
			</p>
		{:else}
			<button
				type="button"
				class="absolute right-3 top-3 text-ash-light transition-colors hover:text-ash"
				onclick={() => respond(false)}
				aria-label="Fermer"
			>
				<X class="size-4" aria-hidden="true" />
			</button>
			<p class="pr-6 text-sm font-medium text-ink">Vous voulez la suite ?</p>
			<p class="mt-1.5 text-sm leading-relaxed text-ash">
				Cochez ce que vous voulez recevoir. Désinscription en un clic.
			</p>
			<div class="mt-3 space-y-2">
				{#if finalites.nouveautes}
					<label class="flex cursor-pointer items-start gap-3">
						<input
							type="checkbox"
							class="mt-0.5 size-4 shrink-0 rounded border-mist text-encre focus:ring-encre"
							bind:checked={nouveautes}
							disabled={saving}
						/>
						<span class="text-sm leading-relaxed text-ash">
							<span class="font-medium text-ink">Les nouveautés</span> —
							1 à 2 emails par mois.
						</span>
					</label>
				{/if}
				{#if finalites.offres}
					<label class="flex cursor-pointer items-start gap-3">
						<input
							type="checkbox"
							class="mt-0.5 size-4 shrink-0 rounded border-mist text-encre focus:ring-encre"
							bind:checked={offres}
							disabled={saving}
						/>
						<span class="text-sm leading-relaxed text-ash">
							<span class="font-medium text-ink">Les offres sur l'abonnement Pro</span> —
							quelques emails par an.
						</span>
					</label>
				{/if}
			</div>
			<div class="mt-4 flex items-center gap-4">
				<button
					type="button"
					class="btn-relief px-4 py-2 text-sm font-semibold"
					onclick={() => respond(true)}
					disabled={saving || (!nouveautes && !offres)}
				>
					{saving ? 'Un instant…' : 'Je veux les recevoir'}
				</button>
				<button
					type="button"
					class="text-sm text-ash transition-colors hover:text-ink disabled:opacity-50"
					onclick={() => respond(false)}
					disabled={saving}
				>
					Non merci
				</button>
			</div>
		{/if}
	</div>
{/if}
